import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers, faRetweet, faSackDollar, faBullhorn, faSpinner, faEye, faStar, faFileCsv,
} from "@fortawesome/free-solid-svg-icons";
import { useAccountType } from "../hooks/useAccountType";
import {
  ppapiPost, walletlessGet, rangeParams, defaultRange, fmtUsd, fmtInt, setViewPartner, downloadText,
} from "../utils/partnerApi";

/**
 * Agency Overview (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.6): totals across every
 * ACCEPTED linked partner + one row per KOL. Drill-down = "view as" that KOL
 * (sidebar switches to his Dashboard / Swap History / Campaigns read-only).
 *
 * Sources: ppapi `agencyOverview {from,to}` (orders/volume/fees per partner),
 * walletless /partner/campaign-referrals + /partner/points-summary per partner
 * (fetched with viewPartnerId, best effort).
 */

const ACCENTS = {
  green: { bg: "rgba(58,209,126,.12)", fg: "#3ad17e" },
  blue: { bg: "rgba(90,162,255,.12)", fg: "#5aa2ff" },
  amber: { bg: "rgba(230,192,106,.12)", fg: "#e6c06a" },
  purple: { bg: "rgba(199,155,255,.12)", fg: "#c79bff" },
};
const StatCard = ({ icon, label, value, sub, accent = "green" }) => {
  const a = ACCENTS[accent] || ACCENTS.green;
  return (
    <div className="spKpi">
      <span className="spKpiIco" style={{ background: a.bg, color: a.fg }}><FontAwesomeIcon icon={icon} /></span>
      <div style={{ minWidth: 0 }}>
        <div className="l">{label}</div>
        <div className="v">{value}</div>
        {sub && <div className="s">{sub}</div>}
      </div>
    </div>
  );
};

const NotAgency = () => (
  <div className="acWrap">
    <div className="acCard" style={{ maxWidth: 560 }}>
      <div className="wwIco"><FontAwesomeIcon icon={faBullhorn} /></div>
      <div className="acName">Agency tools</div>
      <p className="secLead">
        This section is for agency accounts that monitor several KOL partners. If you run an agency,
        reach out on Discord and we will switch your account.
      </p>
      <Link to="/overview" className="btnGhost">Back to dashboard</Link>
    </div>
  </div>
);

const AgencyOverview = () => {
  const accountType = useAccountType();
  const navigate = useNavigate();
  const [{ from, to }, setRange] = useState(() => defaultRange(30));
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState({}); // partnerId -> { referredWallets, activeTraders, points }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    ppapiPost("agencyOverview", rangeParams(from, to))
      .then(async (d) => {
        setData(d || null);
        const partners = Array.isArray(d?.partners) ? d.partners : [];
        // Referral / points per linked partner — best effort, never blocks the table.
        const results = await Promise.allSettled(partners.map(async (p) => {
          const [r, pts] = await Promise.allSettled([
            walletlessGet("/partner/campaign-referrals", { ...rangeParams(from, to), viewPartnerId: p.partnerId }, { view: false }),
            walletlessGet("/partner/points-summary", { viewPartnerId: p.partnerId }, { view: false }),
          ]);
          return [p.partnerId, {
            referredWallets: r.status === "fulfilled" ? Number(r.value?.totals?.referredWallets) || 0 : 0,
            activeTraders: r.status === "fulfilled" ? Number(r.value?.totals?.activeTraders) || 0 : 0,
            points: pts.status === "fulfilled" ? Number(pts.value?.referralBonusPoints) || 0 : 0,
          }];
        }));
        setExtras(Object.fromEntries(results.filter((x) => x.status === "fulfilled").map((x) => x.value)));
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { if (accountType === "agency") load(); }, [accountType, load]);

  const rows = useMemo(() => (Array.isArray(data?.partners) ? data.partners : []).map((p) => ({
    ...p,
    orders: Number(p.orders) || 0, volumeUsd: Number(p.volumeUsd) || 0, feesUsd: Number(p.feesUsd) || 0,
    ...(extras[p.partnerId] || { referredWallets: 0, activeTraders: 0, points: 0 }),
  })).sort((a, b) => b.feesUsd - a.feesUsd), [data, extras]);

  const totals = useMemo(() => rows.reduce((t, r) => ({
    orders: t.orders + r.orders, volumeUsd: t.volumeUsd + r.volumeUsd, feesUsd: t.feesUsd + r.feesUsd,
    referredWallets: t.referredWallets + r.referredWallets, activeTraders: t.activeTraders + r.activeTraders, points: t.points + r.points,
  }), { orders: 0, volumeUsd: 0, feesUsd: 0, referredWallets: 0, activeTraders: 0, points: 0 }), [rows]);

  const chartData = useMemo(() => {
    const shown = rows.filter((r) => r.feesUsd > 0).slice(0, 10);
    return { labels: shown.map((r) => r.partnerName), datasets: [{ label: "Fees (USD)", data: shown.map((r) => r.feesUsd), backgroundColor: "#3ad17e", borderRadius: 6, maxBarThickness: 44 }] };
  }, [rows]);
  const chartOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: "#151617", borderColor: "rgba(255,255,255,.15)", borderWidth: 1, callbacks: { label: (ctx) => fmtUsd(ctx.parsed.y) } } },
    scales: { x: { ticks: { color: "#7c847e" }, grid: { color: "rgba(255,255,255,0.04)" } }, y: { ticks: { color: "#7c847e", callback: (v) => fmtUsd(v) }, grid: { color: "rgba(255,255,255,0.06)" } } },
  }), []);

  const viewAs = (p) => {
    setViewPartner({ id: p.partnerId, name: p.partnerPublicName || p.partnerName });
    navigate("/overview");
  };

  const exportCsv = () => {
    const header = ["partner", "partner_id", "orders", "volume_usd", "fees_usd", "referred_wallets", "active_traders", "referral_points"];
    const lines = rows.map((r) => [`"${String(r.partnerPublicName || r.partnerName).replace(/"/g, '""')}"`, r.partnerId, r.orders, r.volumeUsd.toFixed(2), r.feesUsd.toFixed(2), r.referredWallets, r.activeTraders, r.points].join(","));
    downloadText(`agency-overview_${from}_${to}.csv`, [header.join(","), ...lines].join("\n"));
  };

  if (accountType !== "agency") return <NotAgency />;

  const cols = "minmax(180px,1.4fr) 90px 90px 100px 130px 120px 90px 90px";

  return (
    <>
      <Helmet><title>Agency Overview | Instaswap Partners</title></Helmet>
      <div>
        <div className="secHdr" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h2 className="secTitle">Agency Overview</h2>
            <p className="secLead">Everything your linked KOLs generated in the selected range. Click a KOL to open his dashboard read-only.</p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label className="fldLbl" style={{ fontSize: 11 }}>From</label>
              <input className="input" type="date" value={from} max={to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} style={{ height: 38, fontSize: 12.5, width: 150 }} />
            </div>
            <div>
              <label className="fldLbl" style={{ fontSize: 11 }}>To</label>
              <input className="input" type="date" value={to} min={from} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} style={{ height: 38, fontSize: 12.5, width: 150 }} />
            </div>
            <button type="button" className="btnGhost sm" onClick={exportCsv} disabled={loading || rows.length === 0} style={{ height: 38 }}>
              <FontAwesomeIcon icon={faFileCsv} /> CSV
            </button>
          </div>
        </div>

        <div className="spKpis">
          <StatCard icon={faBullhorn} label="Linked KOLs" value={fmtInt(rows.length)} sub={<Link to="/agency/partners" style={{ color: "#3ad17e" }}>manage partners →</Link>} accent="purple" />
          <StatCard icon={faRetweet} label="Orders" value={fmtInt(totals.orders)} sub="all products" accent="amber" />
          <StatCard icon={faSackDollar} label="Fees earned by KOLs" value={fmtUsd(totals.feesUsd)} sub={`on ${fmtUsd(totals.volumeUsd)} volume`} accent="green" />
          <StatCard icon={faUsers} label="Referred users" value={fmtInt(totals.referredWallets)} sub={`${fmtInt(totals.activeTraders)} active traders`} accent="blue" />
        </div>

        {loading ? (
          <div className="gtEmpty" style={{ marginTop: 24 }}><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : error ? (
          <div className="notice red" style={{ marginTop: 24 }}><span className="ni">⚠</span><div>Could not load the overview. If you just got agency access, sign out and back in.</div></div>
        ) : (
          <>
            {chartData.labels.length > 0 && (
              <div className="panel" style={{ marginTop: 24 }}>
                <div className="pHd"><div><div className="pTitle">Fees by KOL</div><div className="pSub">{from} → {to}</div></div></div>
                <div style={{ height: 280 }}><Chart type="bar" data={chartData} options={chartOptions} /></div>
              </div>
            )}
            <div className="gtScroll" style={{ marginTop: 24 }}>
              <div style={{ minWidth: 960 }}>
                <div className="gtHead" style={{ gridTemplateColumns: cols }}>
                  <span>KOL</span><span className="r">Referred</span><span className="r">Active</span><span className="r">Orders</span><span className="r">Volume</span><span className="r">Fees</span><span className="r">Points</span><span className="r" />
                </div>
                {rows.map((r) => (
                  <div className="gtRow" key={r.partnerId} style={{ gridTemplateColumns: cols }}>
                    <span className="cellStrong">
                      {r.partnerPublicName || r.partnerName}
                      {r.partnerPublicName && r.partnerPublicName !== r.partnerName && <small style={{ display: "block", color: "#7c847e" }}>{r.partnerName}</small>}
                    </span>
                    <span className="r">{fmtInt(r.referredWallets)}</span>
                    <span className="r">{fmtInt(r.activeTraders)}</span>
                    <span className="r">{fmtInt(r.orders)}</span>
                    <span className="r cellMono">{fmtUsd(r.volumeUsd)}</span>
                    <span className="r cellFee">{fmtUsd(r.feesUsd)}</span>
                    <span className="r"><FontAwesomeIcon icon={faStar} style={{ color: "#e6c06a", marginRight: 4 }} />{fmtInt(r.points)}</span>
                    <span className="r">
                      <button type="button" className="iconBtn edit" title="View as this KOL" onClick={() => viewAs(r)}><FontAwesomeIcon icon={faEye} /></button>
                    </span>
                  </div>
                ))}
                {rows.length === 0 && (
                  <div className="gtEmpty">
                    No linked partners yet. <Link to="/agency/partners" style={{ color: "#3ad17e" }}>Link a partner</Link> or <Link to="/agency/invites" style={{ color: "#3ad17e" }}>create an invite link</Link>.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AgencyOverview;
